import MiniCartAccessories from "@NA/CustomComponents/MiniCartAccessories";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainer, GridItem, Typography } from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import MiniMuiltpleCartLine from "@NA/CustomComponents/MiniMuiltpleCartLine";
import { ItemList } from "@insite/content-library/Widgets/Basic/ItemList";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";

interface OwnProps {
    isModalOpen: boolean;
    onModalClose: () => void;
    cartLines: CartLineModel[];
    productIdArray: any;
    extendedStyles?: MiniCartModalStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface MiniCartModalStyles {
    modal?: ModalPresentationProps;
    viewCartShoppingWrapper?: InjectableCss;
    viewCartButton?: ButtonPresentationProps;
    continueShoppingButton?: ButtonPresentationProps;
    container?: GridContainerProps;
    orderLineInfoTitleContainer?: GridContainerProps;
    orderLineInfoTitleGridItem?: GridItemProps;
    orderLineInfoItemTitle?: TypographyPresentationProps;
    orderLineInfoTitles?: TypographyPresentationProps;
    orderLineInfoGridItem?: GridItemProps;
}

export const miniCartModalStyles: MiniCartModalStyles = {
    orderLineInfoGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    orderLineInfoTitles: {
        weight: "bold",
        size: 14,
        css: css`
            margin-bottom: 8.5px;
        `,
    },
    orderLineInfoTitleGridItem: {
        width: [0, 0, 0, 12, 12],
    },
    orderLineInfoTitleContainer: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    orderLineInfoItemTitle: {
        weight: "bold",
        size: 14,
        css: css`
            margin-bottom: 8.5px;
            padding-left: 15px;
        `,
    },
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-top: 13px;
        `,
    },
    modal: {
        cssOverrides: {
            scrim: css`
                z-index: 9999;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 0;
            `,
            modalContent: css`
                padding: 0;
            `,
            modalBody: css`
                border-radius: 6px;
                box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.14);
                padding: 28px 20px 33px 20px;
                border: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-width: 98% !important;
                            height: 95% !important;
                        `,
                        css`
                            max-width: 95% !important;
                            height: 95% !important;
                        `,
                        css`
                            max-width: 70% !important;
                            max-height: 700px;
                            overflow: scroll;
                        `,
                        css`
                            max-width: 1185px !important;
                        `,
                        css`
                            max-width: 1185px !important;
                        `,
                    ])}
            `,
            headlineTypography: css`
                font-size: 32px;
                margin-bottom: 0;
            `,
        },
    },
    viewCartShoppingWrapper: {
        css: css`
            padding: 20px 0px 18px 0;
            justify-content: flex-end;
            display: flex;
            border-bottom: 1px solid ${getColor("common.border")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 10px;
                    `,
                    css`
                        margin-top: 20px;
                    `,
                    css`
                        margin-top: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    viewCartButton: {
        css: css`
            margin-right: 20px;
            white-space: nowrap;
        `,
    },
    continueShoppingButton: {
        css: css`
            white-space: nowrap;
        `,
    },
};

const MiniMultipleProductModal = ({
    isModalOpen,
    onModalClose,
    history,
    cartLines,
    cartUrl,
    extendedStyles,
    productIdArray,
    cart,
}: Props) => {
    const [styles] = useState(() => mergeToNew(miniCartModalStyles, extendedStyles));

   
    const [newCartLines, setNewCartLines] = useState<CartLineModel[] | null>(null);

    useEffect(() => {
        if (cart && cart?.cartLines) {
            const filteredCartLine = cart.cartLines.filter(item => {
                if (item.productId && productIdArray.includes(item.productId)) return ItemList;
            });
            setNewCartLines(filteredCartLine);
        }
    }, [cart, cart?.cartLines, productIdArray]);

    if (!cart || !cart.cartLines) {
        return null;
    }

    const onClick = (link: string | undefined) => {
        if (link) {
            history.push(link);
        }
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Added To Cart")}
            isOpen={isModalOpen}
            handleClose={onModalClose}
            data-test-selector="cart_miniCartModal"
        >
            <GridContainer {...styles.container}>
                <GridItem {...styles.orderLineInfoTitleGridItem}>
                    <GridContainer {...styles.orderLineInfoTitleContainer}>
                        <GridItem width={6}>
                            <Typography {...styles.orderLineInfoItemTitle} as="h4">
                                {translate("Item")}
                            </Typography>
                        </GridItem>
                        <GridItem width={3}>
                            <Typography {...styles.orderLineInfoTitles} as="h4">
                                {translate("Price")}
                            </Typography>
                        </GridItem>
                        <GridItem width={1}>
                            <Typography {...styles.orderLineInfoTitles} as="h4">
                                {translate("Qty")}
                            </Typography>
                        </GridItem>
                        <GridItem width={2}>
                            <Typography {...styles.orderLineInfoTitles} as="h4">
                                {translate("Total")}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.orderLineInfoGridItem}>
                    {newCartLines &&
                        newCartLines.length > 0 &&
                        newCartLines.map(item => <MiniMuiltpleCartLine cartLine={item} />)}
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.viewCartShoppingWrapper}>
                <Button variant="secondary" {...styles.viewCartButton} onClick={() => onClick(cartUrl)}>
                    {translate("View Cart")}
                </Button>
                <Button variant="primary" {...styles.continueShoppingButton} onClick={onModalClose}>
                    {translate("Continue Shopping")}
                </Button>
            </StyledWrapper>
            {<MiniCartAccessories cartLine={cartLines[0]} />}
        </Modal>
    );
};

export default connect(mapStateToProps)(withHistory(MiniMultipleProductModal));
